import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { SELECT_FIELD_DEFAULT_VALUE } from '../../../constants/settings';
import useLanguageActions from '../../../hooks/APIHooks/users/useLanguageActions';
import { SelectData } from '../../../types/common';
import { AllLanguage, Language } from '../../../types/languages';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import SkillBadge from '../Badges/SkillBadge';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const AddLanguageModal = ({
  handleModalClose,
}: {
  handleModalClose: () => void;
}) => {
  // integration of react hooks here
  const [selectedLanguages, setSelectedLanguages] = useState<Array<Language>>(
    []
  );
  const [selectedLang, setSelectedLang] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );

  const [resetLangSelect, setResetLangSelect] = useState<boolean>(false);

  // integration of tanstack query here
  const queryClient = useQueryClient();

  // integration of custom API hooks here
  const {
    getAddedLanguagesHandler,
    getAllLanguagesHandler,
    addNewLanguagesHandler,
  } = useLanguageActions('allLanguages');

  console.log(
    'LANG MODAL DATA: ',
    getAddedLanguagesHandler.data,
    getAllLanguagesHandler.data
  );

  // adding selecting language into the selected array here
  useEffect(() => {
    if (selectedLang.id !== -1) {
      setSelectedLanguages((state) => [
        ...state,
        {
          id: selectedLang.id,
          name: selectedLang.name,
          code: selectedLang.code as string,
        },
      ]);

      setSelectedLang(SELECT_FIELD_DEFAULT_VALUE);
    }
  }, [selectedLang]);

  // handler function to remove language from list declared here
  const removeLanguageHandler = (langId: number) => {
    setSelectedLanguages((state) => state.filter((lang) => lang.id !== langId));
  };

  // function to filter out languages declared here
  const filterOutLanguages = useCallback(
    (lang: AllLanguage) => {
      return !getAddedLanguagesHandler.data?.data
        .concat(selectedLanguages)
        .some((selectedLang) => selectedLang.code === lang.code);
    },
    [getAddedLanguagesHandler.data?.data, selectedLanguages]
  );

  const allLanguageList = useMemo(() => {
    if (!getAllLanguagesHandler.isSuccess) return [];

    return getAllLanguagesHandler.data.data
      .filter(filterOutLanguages)
      .map((lang, index) => ({
        id: index,
        name: lang.name,
        code: lang.code,
      }));
  }, [
    getAllLanguagesHandler.data,
    filterOutLanguages,
    getAllLanguagesHandler.isSuccess,
  ]);

  // handler function to add new language(s) into the database declared here
  const addLanguageHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      await addNewLanguagesHandler
        .mutateAsync({
          body: selectedLanguages.map((lang) => ({
            name: lang.name,
            code: lang.code,
          })),
        })
        .then((res) => {
          console.log(res);
          if (res.is_success) {
            toast.success('Languages Added!!');

            handleModalClose();
            queryClient.invalidateQueries({ queryKey: ['added-languages'] });
          } else {
            toast.error(res.message || 'Language Addition Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering add language modal component here
  return (
    <ParentModal onClick={handleModalClose}>
      <ModalTitle titleText='add language' />

      <Formik initialValues={{ languages: '' }} onSubmit={addLanguageHandler}>
        {(props) => (
          <Form className='space-y-5'>
            <AcademiaFormSelect
              name='languages'
              label='Select Language'
              options={allLanguageList}
              defaultValue={selectedLang}
              setValueOnChange={(value) => {
                setSelectedLang(value);
                setResetLangSelect(true);
              }}
              resetSelectField={resetLangSelect}
              showSearchInput
              isRequired
            />

            {selectedLanguages.length ? (
              <div className='flex gap-2 flex-wrap border-2 border-base-cyan rounded-lg p-3'>
                {selectedLanguages.map((lang) => (
                  <SkillBadge
                    key={lang.id}
                    badgeLabel={lang.name}
                    color='#845ADF'
                    isEditing
                    onClickRemove={() => removeLanguageHandler(lang.id)}
                  />
                ))}
              </div>
            ) : null}

            <AcademiaButton
              title={
                !selectedLanguages.length
                  ? 'Please, select at least one language'
                  : ''
              }
              type='submit'
              variant='premium'
              icon={<TbSquareRoundedPlusFilled className='size-6' />}
              isDisabled={!selectedLanguages.length}
              isLoading={props.isSubmitting}
              fullWidth
            >
              Add Languages
            </AcademiaButton>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default AddLanguageModal;
